import type { Ref } from 'vue'

/**
 * A native dialog that leaves the way it came: closing first adds `closingClass`, whose styles play
 * the way out (on the dialog and its ::backdrop), and closes the dialog once that has run.
 * Escape goes the same way (use `onCancel` on the dialog's cancel event). With reduced motion it simply closes.
 */
export function useDialogExit (dialog: Ref<HTMLDialogElement | undefined>, closingClass: string) {
  let closing = false

  function close () {
    const element = dialog.value
    if (!element?.open || closing) { return }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      element.close()
      return
    }
    closing = true
    element.classList.add(closingClass)
    let fallback = 0
    const done = () => {
      clearTimeout(fallback)
      element.removeEventListener('animationend', onEnd)
      element.classList.remove(closingClass)
      closing = false
      element.close()
    }
    // The dialog's own animation, not one of something inside it
    const onEnd = (event: AnimationEvent) => { if (event.target === element) { done() } }
    element.addEventListener('animationend', onEnd)
    // In case no animation runs
    fallback = window.setTimeout(done, 700)
  }

  // Escape: the same way out instead of disappearing at once
  function onCancel (event: Event) {
    event.preventDefault()
    close()
  }

  return { close, onCancel }
}
